import type { NavLinkProps } from 'react-router-dom'
import type { ImportStatement } from 'src/types/common/component'

import commonRepo from '@/repository/common.repo'
import WebviewService from '@/service/webview.service'

type PreAction = 'onMouseEnter' | 'onMouseDown'
type LinkPreloadParams<T> = {
  to: NavLinkProps['to']
  importStatement?: ImportStatement<T>
  matchUrl?: string
  preAction?: PreAction
  condition?: (param) => boolean
}

// 호버 프리로드 실패는 클릭 경로의 청크 로드 실패 처리가 맡으므로 여기서는 버린다
export const preloadModule = (load?: () => Promise<unknown>) => {
  load?.().catch(() => undefined)
}

export function onLinkPreload<T>({
  to,
  matchUrl,
  importStatement,
  preAction = 'onMouseEnter',
  condition = () => true,
}: LinkPreloadParams<T>) {
  if (importStatement) {
    const preloadFn = async () => {
      const convertTo = typeof to === 'function' ? to(commonRepo.router.location) : to
      // LocationDescriptor -> string
      const lastTo =
        typeof convertTo === 'string'
          ? convertTo
          : `${convertTo.pathname}${convertTo.search ? '' : convertTo.search}` ?? ''

      if (condition(matchUrl ?? lastTo)) {
        const Component = await importStatement()
        if (Component.default.onMount) {
          Component.default.onMount(lastTo)
        }
      }
    }

    return onPreload({ preloadFn, preAction })
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PreloadParams<PreloadFnT extends (...args: any[]) => any> = {
  preAction?: PreAction
  preloadFn: PreloadFnT
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const onPreload = <FnT extends (...args: any[]) => any>({
  preAction = 'onMouseEnter',
  preloadFn,
}: PreloadParams<FnT>) => {
  return WebviewService.isMobile
    ? { onTouchStart: preloadFn }
    : { [preAction as PreAction]: preloadFn }
}
